import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import styles from './NoteStudy.module.css';
import { EditIcon, HeartIcon, LoaderIcon, ArrowLeftIcon } from '../components/Icons';

const normalizeNotes = (payload) => {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.map((note) => ({
    id: note?.id ?? note?.note_id ?? Math.random().toString(36).slice(2),
    note_content: note?.note_content ?? note?.content ?? '',
    created_at: note?.created_at ?? note?.createdAt ?? null,
    chapter_id: note?.chapter_id ?? note?.chapterId ?? null,
    chapter_title: note?.chapter_title ?? note?.chapterTitle ?? '',
    sentence_id: note?.sentence_id ?? note?.sentenceId ?? null,
    novel_id: note?.novel_id ?? note?.novelId ?? null,
    volume_id: note?.volume_id ?? note?.volumeId ?? null,
  }));
};

const formatDateTime = (value) => {
  if (!value) {
    return '';
  }

  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const NoteStudy = () => {
  const { bookId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const initialStateRef = useRef(location.state);
  const containerRef = useRef(null);
  const noteCardRef = useRef(null);
  const originalCacheRef = useRef(new Map());

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [originalVisible, setOriginalVisible] = useState(false);
  const [originalLoading, setOriginalLoading] = useState(false);
  const [originalError, setOriginalError] = useState('');
  const [originalContent, setOriginalContent] = useState(null);

  useEffect(() => {
    if (location.state) {
      initialStateRef.current = location.state;
    }
  }, [location.state]);

  useEffect(() => {
    const indexParam = searchParams.get('index');
    if (indexParam != null) {
      const parsed = parseInt(indexParam, 10);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        setCurrentIndex(parsed);
      }
    } else {
      setCurrentIndex(0);
    }
  }, [searchParams]);

  useEffect(() => {
    const stateNotes = location.state?.notes;
    if (Array.isArray(stateNotes) && stateNotes.length) {
      const normalized = normalizeNotes(stateNotes);
      setNotes(normalized);
      setLoading(false);
      setError('');
      return;
    }

    if (!bookId) {
      setLoading(false);
      setNotes([]);
      setError('未找到对应的书籍');
      return;
    }

    let isCancelled = false;

    const loadNotes = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.get('/user-notes/', {
          params: { novel_id: bookId },
        });
        console.log('note study fetch response:', response.data);
        if (isCancelled) {
          return;
        }
        const normalized = normalizeNotes(response.data);
        setNotes(normalized);
        if (normalized.length === 0) {
          setError('暂无学习笔记');
        }
      } catch (err) {
        console.error('加载笔记失败:', err);
        if (!isCancelled) {
          setError('加载笔记失败，请稍后再试');
          setNotes([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadNotes();

    return () => {
      isCancelled = true;
    };
  }, [bookId, location.state]);

  useEffect(() => {
    setOriginalVisible(false);
    setOriginalError('');
    setOriginalContent(null);
    setOriginalLoading(false);
  }, [currentIndex]);

  const handleBack = useCallback(() => {
    const initialState = initialStateRef.current;

    if (initialState?.from === 'historyNotes') {
      navigate('/history/notes', {
        state: {
          historyView: initialState?.historyReturn || { activeView: 'notes' },
        },
      });
      return;
    }

    if (initialState?.returnPath) {
      const target = initialState.returnPath;
      if (typeof target === 'string') {
        navigate(target);
        return;
      }
      if (target && typeof target === 'object') {
        const pathname = target.pathname || '';
        if (pathname) {
          const search = target.search || '';
          const hash = target.hash || '';
          navigate(`${pathname}${search}${hash}`, { state: target.state });
          return;
        }
      }
    }

    if (initialState?.from) {
      navigate(initialState.from, {
        state: initialState.historyReturn ? { historyReturn: initialState.historyReturn } : undefined,
      });
      return;
    }

    if (bookId) {
      navigate(`/book/${bookId}`);
      return;
    }

    navigate(-1);
  }, [navigate, bookId]);

  const handleNext = () => {
    if (!notes.length) {
      return;
    }

    if (currentIndex >= notes.length - 1) {
      if (notes.length) {
        setCurrentIndex(0);
        setSearchParams({ index: '0' });
      }
      return;
    }

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    setSearchParams({ index: String(nextIndex) });
  };

  const handlePrevious = () => {
    if (!notes.length) {
      return;
    }

    if (currentIndex <= 0) {
      if (notes.length) {
        const lastIndex = notes.length - 1;
        setCurrentIndex(lastIndex);
        setSearchParams({ index: String(lastIndex) });
      }
      return;
    }

    const nextIndex = currentIndex - 1;
    setCurrentIndex(nextIndex);
    setSearchParams({ index: String(nextIndex) });
  };

  const handleToggleOriginal = async () => {
    const currentNote = notes[currentIndex];
    if (!currentNote?.chapter_id || currentNote.sentence_id == null) {
      return;
    }

    const cacheKey = `${currentNote.chapter_id}-${currentNote.sentence_id}`;

    if (originalVisible) {
      setOriginalVisible(false);
      return;
    }

    setOriginalError('');

    if (originalCacheRef.current.has(cacheKey)) {
      setOriginalContent(originalCacheRef.current.get(cacheKey));
      setOriginalVisible(true);
      return;
    }

    setOriginalLoading(true);
    setOriginalVisible(true);

    try {
      const response = await api.get(`/chapters/${currentNote.chapter_id}/sentences/${currentNote.sentence_id}`);
      const payload = response.data || {};
      const content = {
        zh: payload.content_zh || payload.zh || payload.contentZh || payload.text || '',
        en: payload.content_en || payload.en || payload.contentEn || payload.translation || '',
      };
      originalCacheRef.current.set(cacheKey, content);
      setOriginalContent(content);
    } catch (err) {
      console.error('加载原文句子失败:', err);
      setOriginalError('加载原文句子失败');
      setOriginalContent(null);
    } finally {
      setOriginalLoading(false);
    }
  };

  const handleUnfavorite = async () => {
    const currentNote = notes[currentIndex];
    if (!currentNote?.id) {
      return;
    }

    setFavoriteLoading(true);
    try {
      await api.delete(`/user-notes/${currentNote.id}`);

      const updatedNotes = notes.filter((note) => note.id !== currentNote.id);
      setNotes(updatedNotes);

      if (updatedNotes.length === 0) {
        handleBack();
        return;
      }

      const nextIndex = Math.min(currentIndex, updatedNotes.length - 1);
      setCurrentIndex(nextIndex);
      setSearchParams({ index: String(nextIndex) });
    } catch (err) {
      console.error('取消收藏失败:', err);
      alert('取消收藏失败，请稍后重试');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleContainerNavigation = (event) => {
    if (event.defaultPrevented) {
      return;
    }

    const interactiveTarget = event.target.closest(
      'button, a, input, textarea, select, label, [data-prevent-nav="true"]'
    );
    if (interactiveTarget) {
      return;
    }

    const nativeEvent = event.nativeEvent;

    let clientX;
    if (nativeEvent && nativeEvent.changedTouches && nativeEvent.changedTouches.length > 0) {
      clientX = nativeEvent.changedTouches[0].clientX;
    } else if (event.clientX != null) {
      clientX = event.clientX;
    } else if (nativeEvent && nativeEvent.clientX != null) {
      clientX = nativeEvent.clientX;
    }

    if (clientX == null) {
      console.log('[NoteStudy] Navigation skipped: missing clientX');
      return;
    }

    const container = containerRef.current || event.currentTarget;
    if (!container || typeof container.getBoundingClientRect !== 'function') {
      console.log('[NoteStudy] Navigation skipped: missing container rect');
      return;
    }

    const rect = container.getBoundingClientRect();
    if (!rect || rect.width === 0) {
      console.log('[NoteStudy] Navigation skipped: invalid rect', rect);
      return;
    }

    const clickX = clientX - rect.left;

    console.log('[NoteStudy] handleContainerNavigation', {
      eventType: event.type,
      clientX,
      clickX,
      rectLeft: rect.left,
      containerWidth: rect.width,
    });

    if (!Number.isFinite(clickX)) {
      console.log('[NoteStudy] Navigation skipped: clickX not finite');
      return;
    }

    if (clickX < rect.width / 2) {
      console.log('[NoteStudy] Navigating to previous note');
      handlePrevious();
    } else {
      console.log('[NoteStudy] Navigating to next note');
      handleNext();
    }
  };

  const currentNote = notes[currentIndex] || null;

  const noteParagraphs = useMemo(() => {
    if (!currentNote?.note_content) {
      return [];
    }
    return currentNote.note_content
      .split(/\r?\n+/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }, [currentNote]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <LoaderIcon size={32} />
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error || notes.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>{error || '暂无学习笔记'}</p>
          <button type="button" className={styles.backBtn} onClick={handleBack} aria-label="返回收藏笔记列表">
          <ArrowLeftIcon size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      ref={containerRef}
      onClick={handleContainerNavigation}
      onTouchStart={handleContainerNavigation}
      role="presentation"
    >
      <div className={styles.header}>
        <button type="button" className={styles.backBtn} onClick={handleBack} aria-label="返回收藏笔记列表">
        <ArrowLeftIcon size={20} />

        </button>
        <div className={styles.headerRight}>
          <div className={styles.progress}>
            {currentIndex + 1} / {notes.length}
          </div>
        </div>
      </div>

      <div
        className={styles.noteCard}
        ref={noteCardRef}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            handlePrevious();
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            handleNext();
          }
        }}
      >
     
        {(currentNote?.chapter_id && currentNote?.sentence_id != null) && (
          <div className={styles.noteActions}>
            <button
              type="button"
              className={styles.toggleOriginalBtn}
              onClick={handleToggleOriginal}
              disabled={originalLoading}
              aria-expanded={originalVisible}
            >
              {originalVisible ? '隐藏原文' : originalLoading ? '原文加载中…' : '查看原文'}
            </button>
            <button
              type="button"
              className={styles.unfavoriteBtn}
              onClick={handleUnfavorite}
              disabled={favoriteLoading}
              title="取消收藏"
              aria-label="取消收藏"
            >
              {favoriteLoading ? <LoaderIcon size={18} /> : <HeartIcon size={22} filled />}
            </button>
          </div>
        )}
 {originalVisible && (
          <div className={styles.originalSection}>
            {originalLoading ? (
              <div className={styles.originalStatus}>原文加载中…</div>
            ) : originalError ? (
              <div className={styles.originalError}>{originalError}</div>
            ) : originalContent && (originalContent.zh || originalContent.en) ? (
              <>
                {originalContent.zh && (
                  <div className={styles.originalBlock}>
                    <p className={styles.originalText}>{originalContent.zh}</p>
                  </div>
                )}
                {originalContent.en && (
                  <div className={styles.originalBlock}>
                    <p className={styles.originalText}>{originalContent.en}</p>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.originalEmpty}>未找到原文句子</div>
            )}
          </div>
        )}
        <div className={styles.noteContent}>
          {noteParagraphs.length ? (
            noteParagraphs.map((paragraph, index) => (
              <p key={`note-paragraph-${currentNote.id}-${index}`} className={styles.noteParagraph}>
                {paragraph}
              </p>
            ))
          ) : (
            <p className={styles.noteParagraphEmpty}>这条笔记没有内容</p>
          )}
        </div>
        <div className={styles.noteMeta}>
          {currentNote?.chapter_title && (
            <span className={styles.chapterTitle}>{currentNote.chapter_title}</span>
          )}
          {/* {currentNote?.chapter_id && !currentNote?.chapter_title && (
            <span className={styles.chapterTitle}>章节 {currentNote.chapter_id}</span>
          )} */}
          <span className={styles.noteTimestamp}>{formatDateTime(currentNote?.created_at)}</span>
        </div>
      </div>
      <div className={styles.footerNavFixed}>
        <button
          type="button"
          className={styles.navBtn}
          onClick={handlePrevious}
          disabled={!notes.length}
          aria-label="上一条笔记"
        >
          上一条
        </button>
        <button
          type="button"
          className={styles.navBtn}
          onClick={handleNext}
          disabled={!notes.length}
          aria-label="下一条笔记"
        >
          下一条
        </button>
      </div>
    </div>
  );
};

export default NoteStudy;

